// src/app/pages/asistencias/store/asistencia.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import {
  map,
  mergeMap,
  catchError,
  tap,
  switchMap,
  withLatestFrom,
  filter,
  take, // 🔥 Importar 'take'
  debounceTime, // 🔥 Importar 'debounceTime'
} from 'rxjs/operators';
import { AsistenciaService } from '@app/services/asistencia.service';
import Swal from 'sweetalert2';
import moment from 'moment';
import { environment } from '@src/environments/environment';

import * as fromRoot from '@app/store';
import * as fromUser from '@app/store/user';

import {
  AsistenciaActionTypes,
  IniciarBreak,
  IniciarBreakSuccess,
  IniciarBreakFailure,
  FinalizarBreak,
  FinalizarBreakSuccess,
  FinalizarBreakFailure,
  IniciarBano,
  IniciarBanoSuccess,
  IniciarBanoFailure,
  FinalizarBano,
  FinalizarBanoSuccess,
  FinalizarBanoFailure,
  IniciarAsistencia,
  IniciarAsistenciaSuccess,
  IniciarAsistenciaFailure,
  MarcarSalida,
  MarcarSalidaSuccess,
  MarcarSalidaFailure,
  LoadAsistenciasAsesorOptimizado,
  LoadAsistenciasAsesorOptimizadoSuccess,
  LoadAsistenciasAsesorOptimizadoFailure,
  VerificarEstadoActividades,
  VerificarEstadoActividadesSuccess,
  SetError,
  AsesorPaginaCargada,
  HeaderRefrescarEstado,
  HeaderMenuAbierto,
} from './asistencia.actions';

import * as AsistenciaSelectors from './asistencia.reducer';
import { Notificacion } from '../../../models/notificacion.model';

@Injectable()
export class AsistenciaEffects {
  constructor(
    private actions$: Actions,
    private asistenciaService: AsistenciaService,
    private store: Store<fromRoot.State>,
    private router: Router,
  ) {}

  // 🔥 EFFECT ORQUESTADOR PRINCIPAL (CORREGIDO Y ÚNICO PUNTO DE ENTRADA)
  // Se activa cuando la página de asistencias del asesor es cargada.
  asesorPaginaCargada$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.ASESOR_PAGINA_CARGADA),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      switchMap(([action, user]) => {
        if (!user || !user.id) {
          return of({ type: '[Asistencia] No User, No Operation' });
        }

        const usuarioId = user.id;
        const fechaInicio = moment().startOf('month').format('YYYY-MM-DD');
        const fechaFin = moment().endOf('month').format('YYYY-MM-DD');

        // 1. PRIMERO: Obtener el estado de la actividad en tiempo real.
        return this.asistenciaService.obtenerEstadoActividades(usuarioId).pipe(
          mergeMap((estadoResponse) => {
            if (estadoResponse.rpta !== 1) {
              throw new Error('No se pudo obtener el estado de la actividad.');
            }
            // 2. SEGUNDO: Obtener los datos para el calendario.
            return this.asistenciaService
              .obtenerAsistenciasAsesorOptimizado(
                usuarioId,
                fechaInicio,
                fechaFin
              )
              .pipe(
                // 3. TERCERO: Despachar AMBAS acciones de éxito con datos frescos.
                map((optimizadasResponse) => {
                  return [
                    new VerificarEstadoActividadesSuccess(estadoResponse.data),
                    new LoadAsistenciasAsesorOptimizadoSuccess(
                      optimizadasResponse.data || []
                    ),
                  ];
                }),
                // Aplanar el array de acciones para que el store las reciba.
                mergeMap((actions) => actions)
              );
          }),
          catchError((error) => {
            return of(
              new SetError('Error al sincronizar los datos de asistencia.')
            );
          })
        );
      })
    )
  );

  // 🔥 NUEVO: Effect para refrescar header después de acciones que afecten el estado
  // 🔥 CORREGIDO: Removido LOAD_ASISTENCIAS_ASESOR_OPTIMIZADO_SUCCESS para evitar llamadas innecesarias
  refrescarHeaderDespuesDeAccion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AsistenciaActionTypes.INICIAR_BREAK_SUCCESS,
        AsistenciaActionTypes.FINALIZAR_BREAK_SUCCESS,
        AsistenciaActionTypes.INICIAR_BANO_SUCCESS,
        AsistenciaActionTypes.FINALIZAR_BANO_SUCCESS,
        AsistenciaActionTypes.MARCAR_SALIDA_SUCCESS
      ),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      filter(([action, user]) => !!user && !!user.id),
      map(([action, user]) => {
        console.log('🔄 RefrescarHeader: Acción detectada, refrescando header para usuario', user!.id);
        return new VerificarEstadoActividades({ usuarioId: user!.id });
      })
    )
  );

  // 🔥 ELIMINADO: recargarDespuesDeAccion$ - era duplicado de refrescarHeaderDespuesDeAccion$

  // ========================================
  // 🔥 EFFECTS DE ACCIONES INDIVIDUALES (SIN CAMBIOS)
  // ========================================

  iniciarBreak$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.INICIAR_BREAK),
      mergeMap((action: IniciarBreak) => {
        return this.asistenciaService
          .iniciarBreakHeader(action.payload.usuarioId)
          .pipe(
            map((response) => {
              if (response.rpta === 1) {
                Swal.fire({
                  title: '✅ Break Iniciado',
                  text: 'Su break ha sido iniciado correctamente.',
                  icon: 'success',
                  timer: 2000,
                  timerProgressBar: true,
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                });
                return new IniciarBreakSuccess(response.data);
              } else {
                const mensaje =
                  response.msg ||
                  response.message ||
                  'No se pudo iniciar el break';
                Swal.fire({
                  title: '❌ Error al Iniciar Break',
                  text: mensaje,
                  icon: 'error',
                  confirmButtonText: 'Entendido',
                  confirmButtonColor: '#dc3545',
                });
                return new IniciarBreakFailure(mensaje);
              }
            }),
            catchError((error) => {
              const mensaje =
                error.error?.msg ||
                error.error?.message ||
                'Error de conexión al iniciar break';
              Swal.fire({
                title: '❌ Error al Iniciar Break',
                text: mensaje,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#dc3545',
              });
              return of(new IniciarBreakFailure(mensaje));
            })
          );
      })
    )
  );

  finalizarBreak$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.FINALIZAR_BREAK),
      withLatestFrom(
        this.store.select(AsistenciaSelectors.getTiempoBreakActual)
      ),
      mergeMap(([action, tiempoActual]) => {
        const finalizarAction = action as FinalizarBreak;
        return this.asistenciaService
          .finalizarBreakHeader(finalizarAction.payload.usuarioId)
          .pipe(
            map((response) => {
              if (response.rpta === 1) {
                const excedido = tiempoActual > 15;
                const titulo = excedido
                  ? '⚠️ Break Finalizado (Excedido)'
                  : '✅ Break Finalizado';
                const mensaje = excedido
                  ? 'Break finalizado. Procure respetar los límites de tiempo.'
                  : 'Su break ha sido finalizado correctamente.';
                Swal.fire({
                  title: titulo,
                  text: mensaje,
                  icon: excedido ? 'warning' : 'success',
                  timer: 2500,
                  timerProgressBar: true,
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                });
                return new FinalizarBreakSuccess(response.data);
              } else {
                const mensaje =
                  response.msg ||
                  response.message ||
                  'No se pudo finalizar el break';
                Swal.fire({
                  title: '❌ Error al Finalizar Break',
                  text: mensaje,
                  icon: 'error',
                  confirmButtonText: 'Entendido',
                  confirmButtonColor: '#dc3545',
                });
                return new FinalizarBreakFailure(mensaje);
              }
            }),
            catchError((error) => {
              const mensaje =
                error.error?.msg ||
                error.error?.message ||
                'Error de conexión al finalizar break';
              Swal.fire({
                title: '❌ Error al Finalizar Break',
                text: mensaje,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#dc3545',
              });
              return of(new FinalizarBreakFailure(mensaje));
            })
          );
      })
    )
  );

  iniciarBano$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.INICIAR_BANO),
      mergeMap((action: IniciarBano) =>
        this.asistenciaService.iniciarBanoHeader(action.payload.usuarioId).pipe(
          map((response) => {
            if (response.rpta === 1) {
              Swal.fire({
                title: '🚽 Ida al Baño Registrada',
                text: 'Su ida al baño ha sido registrada.',
                icon: 'success',
                timer: 2000,
                timerProgressBar: true,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
              });
              return new IniciarBanoSuccess(response.data);
            } else {
              const mensaje =
                response.msg ||
                response.message ||
                'No se pudo registrar la ida al baño';
              Swal.fire({
                title: '❌ Error al Registrar Ida al Baño',
                text: mensaje,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#dc3545',
              });
              return new IniciarBanoFailure(mensaje);
            }
          }),
          catchError((error) => {
            const mensaje =
              error.error?.msg ||
              error.error?.message ||
              'Error de conexión al registrar ida al baño';
            Swal.fire({
              title: '❌ Error al Registrar Ida al Baño',
              text: mensaje,
              icon: 'error',
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#dc3545',
            });
            return of(new IniciarBanoFailure(mensaje));
          })
        )
      )
    )
  );

  finalizarBano$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.FINALIZAR_BANO),
      withLatestFrom(
        this.store.select(AsistenciaSelectors.getTiempoBanoActual)
      ),
      mergeMap(([action, tiempoActual]) => {
        const finalizarAction = action as FinalizarBano;
        return this.asistenciaService
          .finalizarBanoHeader(finalizarAction.payload.usuarioId)
          .pipe(
            map((response) => {
              if (response.rpta === 1) {
                const excedido = tiempoActual > 10;
                const titulo = excedido
                  ? '⚠️ Regreso Registrado (Excedido)'
                  : '✅ Regreso Registrado';
                const mensaje = excedido
                  ? 'Regreso registrado. Procure respetar los límites de tiempo.'
                  : 'Su regreso del baño ha sido registrado.';
                Swal.fire({
                  title: titulo,
                  text: mensaje,
                  icon: excedido ? 'warning' : 'success',
                  timer: 2500,
                  timerProgressBar: true,
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                });
                return new FinalizarBanoSuccess(response.data);
              } else {
                const mensaje =
                  response.msg ||
                  response.message ||
                  'No se pudo registrar el regreso del baño';
                Swal.fire({
                  title: '❌ Error al Registrar Regreso',
                  text: mensaje,
                  icon: 'error',
                  confirmButtonText: 'Entendido',
                  confirmButtonColor: '#dc3545',
                });
                return new FinalizarBanoFailure(mensaje);
              }
            }),
            catchError((error) => {
              const mensaje =
                error.error?.msg ||
                error.error?.message ||
                'Error de conexión al registrar regreso del baño';
              Swal.fire({
                title: '❌ Error al Registrar Regreso',
                text: mensaje,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#dc3545',
              });
              return of(new FinalizarBanoFailure(mensaje));
            })
          );
      })
    )
  );

  // 🔥 NUEVO: Effect para manejar HeaderRefrescarEstado (solo para casos específicos)
  headerRefrescarEstado$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.HEADER_REFRESCAR_ESTADO),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      filter(([action, user]) => !!user && !!user.id),
      map(([action, user]) => {
        return new VerificarEstadoActividades({ usuarioId: user!.id });
      })
    )
  );

  // 🔥 NUEVO: Effect específico para cuando se abre el menú del header
  headerMenuAbierto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.HEADER_MENU_ABIERTO),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      filter(([action, user]) => !!user && !!user.id),
      // Usar debounceTime para evitar múltiples llamadas rápidas
      debounceTime(300),
      map(([action, user]) => {
        console.log('🔄 Effect: HeaderMenuAbierto - Verificando estado de actividades');
        return new VerificarEstadoActividades({ usuarioId: user!.id });
      })
    )
  );

  // 🔥 NUEVO: Effect específico para LoadAsistenciasAsesorOptimizado
  loadAsistenciasAsesorOptimizado$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.LOAD_ASISTENCIAS_ASESOR_OPTIMIZADO),
      switchMap((action: LoadAsistenciasAsesorOptimizado) => {
        return this.asistenciaService
          .obtenerAsistenciasAsesorOptimizado(
            action.payload.usuarioId,
            action.payload.fechaInicio,
            action.payload.fechaFin
          )
          .pipe(
            map((response) => {
              return new LoadAsistenciasAsesorOptimizadoSuccess(response.data || []);
            }),
            catchError((error) => {
              console.error('❌ LoadAsistenciasAsesorOptimizado: Error al cargar datos:', error);
              return of(new LoadAsistenciasAsesorOptimizadoFailure('Error al cargar asistencias del calendario'));
            })
          );
      })
    )
  );

  // 🔥 NUEVO: Effect para manejar VerificarEstadoActividades
  verificarEstadoActividades$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.VERIFICAR_ESTADO_ACTIVIDADES),
      mergeMap((action: VerificarEstadoActividades) => {
        return this.asistenciaService
          .obtenerEstadoActividades(action.payload.usuarioId)
          .pipe(
            map((response) => {
              if (response.rpta === 1) {
                return new VerificarEstadoActividadesSuccess(response.data);
              } else {
                console.error(
                  '❌ Error en respuesta del API:',
                  response.message
                );
                return new SetError(
                  response.message || 'Error al obtener estado de actividades'
                );
              }
            }),
            catchError((error) => {
              console.error('❌ Error en llamada al API:', error);
              return of(
                new SetError(
                  error.error?.message ||
                    'Error de conexión al verificar estado'
                )
              );
            })
          );
      })
    )
  );

  // 🔥 ELIMINADO: Effect refrescarEstadoDespuesIniciarAsistencia$ 
  // Ya no se refresca automáticamente después de iniciar asistencia para mejor UX

  // 🔥 NUEVO: Effect para refrescar estado después de acciones de baño exitosas
  refrescarEstadoDespuesBano$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AsistenciaActionTypes.INICIAR_BANO_SUCCESS,
        AsistenciaActionTypes.FINALIZAR_BANO_SUCCESS
      ),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      filter(([action, user]) => !!user && !!user.id),
      map(([action, user]) => {
        return new VerificarEstadoActividades({ usuarioId: user!.id });
      })
    )
  );

  // 🔥 NUEVO: Effect para refrescar estado después de acciones de break exitosas
  refrescarEstadoDespuesBreak$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AsistenciaActionTypes.INICIAR_BREAK_SUCCESS,
        AsistenciaActionTypes.FINALIZAR_BREAK_SUCCESS
      ),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      filter(([action, user]) => !!user && !!user.id),
      map(([action, user]) => {
        return new VerificarEstadoActividades({ usuarioId: user!.id });
      })
    )
  );

  // 🔥 NUEVO: Effect para iniciar asistencia manual
  iniciarAsistencia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.INICIAR_ASISTENCIA),
      mergeMap((action: IniciarAsistencia) =>
        this.asistenciaService.registrarEntrada(action.payload.usuarioId).pipe(
          mergeMap((response) => {
            if (response.rpta === 1) {
              Swal.fire({
                title: '✅ ¡Asistencia Iniciada!',
                text: 'Su jornada laboral ha comenzado. ¡Buen día de trabajo!',
                icon: 'success',
                timer: 3000,
                timerProgressBar: true,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
              });
              // 🔥 NUEVO: Después de registrar asistencia, verificar estado completo Y refrescar notificaciones

              // Refrescar notificaciones para mostrar actualización en tiempo real
              setTimeout(() => {
              }, 1000); // Esperar 1 segundo para que el backend procese

              return [
                new IniciarAsistenciaSuccess(response.data),
                // 🔥 ELIMINADO: VerificarEstadoActividades - se manejará desde el componente si es necesario
              ];
            } else {
              const mensaje =
                response.msg ||
                response.message ||
                'No se pudo iniciar la asistencia';
              Swal.fire({
                title: '❌ Error al Iniciar Asistencia',
                text: mensaje,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#dc3545',
              });
              return [new IniciarAsistenciaFailure(mensaje)];
            }
          }),
          catchError((error) => {
            const mensaje =
              error.error?.msg ||
              error.error?.message ||
              'No se pudo iniciar la asistencia. Intente nuevamente.';
            Swal.fire({
              title: '❌ Error al Iniciar Asistencia',
              text: mensaje,
              icon: 'error',
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#dc3545',
            });
            return of(new IniciarAsistenciaFailure(mensaje));
          })
        )
      )
    )
  );

  marcarSalida$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.MARCAR_SALIDA),
      mergeMap((action: MarcarSalida) =>
        this.asistenciaService.registrarSalida(action.payload.usuarioId).pipe(
          map((response) => {
            if (response.rpta === 1) {
              Swal.fire({
                title: '🏠 ¡Salida Marcada!',
                text: 'Su jornada laboral ha finalizado. ¡Gracias por su trabajo!',
                icon: 'success',
                timer: 4000,
                timerProgressBar: true,
                confirmButtonText: 'Excelente',
                confirmButtonColor: '#28a745',
              });

              // 🔥 NUEVO: Refrescar notificaciones después de marcar salida
              setTimeout(() => {
              }, 1000);

              return new MarcarSalidaSuccess(response.data);
            } else {
              const mensaje =
                response.msg ||
                response.message ||
                'No se pudo marcar la salida';
              Swal.fire({
                title: '❌ Error al Marcar Salida',
                text: mensaje,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#dc3545',
              });
              return new MarcarSalidaFailure(mensaje);
            }
          }),
          catchError((error) => {
            const mensaje =
              error.error?.msg ||
              error.error?.message ||
              'Error de conexión al marcar salida';
            Swal.fire({
              title: '❌ Error al Marcar Salida',
              text: mensaje,
              icon: 'error',
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#dc3545',
            });
            return of(new MarcarSalidaFailure(mensaje));
          })
        )
      )
    )
  );

  // 🔥 NUEVO: Effect para refrescar estado después de marcar salida exitosamente
  refrescarEstadoDespuesSalida$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AsistenciaActionTypes.MARCAR_SALIDA_SUCCESS),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      filter(([action, user]) => !!user && !!user.id),
      map(([action, user]) => {
        return new VerificarEstadoActividades({ usuarioId: user!.id });
      })
    )
  );

  // 🔥 NUEVO: Effect para actualizar calendario después de TODAS las acciones desde el header
  // Solo se ejecuta si estamos en la página de asistencias para actualizar el calendario
  actualizarCalendarioDespuesAccionesHeader$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AsistenciaActionTypes.INICIAR_ASISTENCIA_SUCCESS,
        AsistenciaActionTypes.INICIAR_BREAK_SUCCESS,
        AsistenciaActionTypes.FINALIZAR_BREAK_SUCCESS,
        AsistenciaActionTypes.INICIAR_BANO_SUCCESS,
        AsistenciaActionTypes.FINALIZAR_BANO_SUCCESS,
        AsistenciaActionTypes.MARCAR_SALIDA_SUCCESS
      ),
      withLatestFrom(this.store.pipe(select(fromUser.getUser))),
      filter(([action, user]) => {
        // Solo ejecutar si estamos en la página de asistencias
        const currentUrl = this.router.url;
        const enPaginaAsistencias = currentUrl.includes('/asistencias/personal');
        
        // Mapear tipos de acción para logs más claros
        const actionTypeMap: { [key: string]: string } = {
          [AsistenciaActionTypes.INICIAR_ASISTENCIA_SUCCESS]: 'IniciarAsistencia',
          [AsistenciaActionTypes.INICIAR_BREAK_SUCCESS]: 'IniciarBreak',
          [AsistenciaActionTypes.FINALIZAR_BREAK_SUCCESS]: 'FinalizarBreak',
          [AsistenciaActionTypes.INICIAR_BANO_SUCCESS]: 'IniciarBaño',
          [AsistenciaActionTypes.FINALIZAR_BANO_SUCCESS]: 'FinalizarBaño',
          [AsistenciaActionTypes.MARCAR_SALIDA_SUCCESS]: 'MarcarSalida'
        };
        
        const actionType = actionTypeMap[action.type] || 'AccionDesconocida';
        console.log(`🔄 Effect: ${actionType}Success - URL actual:`, currentUrl, 'En página asistencias:', enPaginaAsistencias);
        
        return !!user && !!user.id && enPaginaAsistencias;
      }),
      debounceTime(500), // Pequeño delay para que el backend procese
      map(([action, user]) => {
        const actionTypeMap: { [key: string]: string } = {
          [AsistenciaActionTypes.INICIAR_ASISTENCIA_SUCCESS]: 'asistencia',
          [AsistenciaActionTypes.INICIAR_BREAK_SUCCESS]: 'break',
          [AsistenciaActionTypes.FINALIZAR_BREAK_SUCCESS]: 'break',
          [AsistenciaActionTypes.INICIAR_BANO_SUCCESS]: 'baño',
          [AsistenciaActionTypes.FINALIZAR_BANO_SUCCESS]: 'baño',
          [AsistenciaActionTypes.MARCAR_SALIDA_SUCCESS]: 'salida'
        };
        
        const actionType = actionTypeMap[action.type] || 'acción';
        console.log(`🔄 Effect: Actualizando calendario después de ${actionType}`);
        
        const fechaInicio = moment().startOf('month').format('YYYY-MM-DD');
        const fechaFin = moment().endOf('month').format('YYYY-MM-DD');
        
        return new LoadAsistenciasAsesorOptimizado({
          usuarioId: user!.id,
          fechaInicio: fechaInicio,
          fechaFin: fechaFin,
        });
      })
    )
  );
}
